/*
 * Triply launch urgency bar — rolling 12hr countdown + scroll trigger.
 * Loaded by sections/triply-collection-styles.liquid on the tri-ply-premium template.
 *
 * Per-visitor:
 *   - localStorage key (configurable via data-storage-key) holds an epoch ms expiry.
 *   - If missing or in the past, write a fresh `now + duration_hours` and restart.
 *   - Bar reveals after scrollY crosses (threshold_vh * viewport_height / 100).
 *   - Once revealed in a session, stays sticky.
 */
(function () {
  'use strict';

  if (customElements.get('triply-urgency-bar')) return;

  class TriplyUrgencyBar extends HTMLElement {
    constructor() {
      super();
      this._tick = this._tick.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._revealed = false;
      this._timerId = null;
    }

    connectedCallback() {
      this.storageKey = this.dataset.storageKey || 'solara_triply_launch_expiry';
      this.durationHours = Math.max(1, parseInt(this.dataset.durationHours || '12', 10));
      this.thresholdVh = Math.max(0, parseInt(this.dataset.thresholdVh || '100', 10));
      this.position = this.dataset.position || 'bottom';

      this.elHours = this.querySelector('[data-tub-hours]');
      this.elMinutes = this.querySelector('[data-tub-minutes]');
      this.elSeconds = this.querySelector('[data-tub-seconds]');
      this.closeBtn = this.querySelector('[data-tub-close]');

      this._ensureExpiry();
      this._tick();
      this._timerId = window.setInterval(this._tick, 1000);

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this._dismiss());
      }

      const sessionDismissed = sessionStorage.getItem(this.storageKey + '__dismissed') === '1';
      if (sessionDismissed) {
        this.style.display = 'none';
        return;
      }

      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    disconnectedCallback() {
      if (this._timerId) clearInterval(this._timerId);
      window.removeEventListener('scroll', this._onScroll);
    }

    _ensureExpiry() {
      let expiry = parseInt(localStorage.getItem(this.storageKey) || '0', 10);
      const now = Date.now();
      if (!expiry || expiry <= now) {
        expiry = now + this.durationHours * 60 * 60 * 1000;
        try { localStorage.setItem(this.storageKey, String(expiry)); } catch (e) { /* private mode */ }
      }
      this.expiry = expiry;
    }

    _tick() {
      let remaining = this.expiry - Date.now();
      if (remaining <= 0) {
        // Roll the window forward
        this._ensureExpiry();
        remaining = this.expiry - Date.now();
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      if (this.elHours) this.elHours.textContent = String(h).padStart(2, '0');
      if (this.elMinutes) this.elMinutes.textContent = String(m).padStart(2, '0');
      if (this.elSeconds) this.elSeconds.textContent = String(s).padStart(2, '0');
    }

    _onScroll() {
      if (this._revealed) return;
      const viewport = window.innerHeight || 800;
      const threshold = (this.thresholdVh / 100) * viewport;
      if (window.scrollY >= threshold) {
        this._reveal();
      }
    }

    _reveal() {
      this._revealed = true;
      this.setAttribute('data-visible', 'true');
      const body = document.body;
      if (body) {
        body.setAttribute('data-tub-active', 'true');
        body.setAttribute('data-tub-position', this.position);
      }
      window.removeEventListener('scroll', this._onScroll);
    }

    _dismiss() {
      this.setAttribute('data-visible', 'false');
      try { sessionStorage.setItem(this.storageKey + '__dismissed', '1'); } catch (e) { /* private mode */ }
      const body = document.body;
      if (body) {
        body.removeAttribute('data-tub-active');
        body.removeAttribute('data-tub-position');
      }
      window.setTimeout(() => { this.style.display = 'none'; }, 400);
    }
  }

  // Pick up the position from the inline transform on first render
  // (declared in liquid via data-position derived from settings.position).
  const observer = new MutationObserver(() => { /* no-op, reserved */ });
  observer.disconnect();

  customElements.define('triply-urgency-bar', TriplyUrgencyBar);
})();
